/**
 * Système de logging centralisé
 * 
 * Phase 3.3 : Centralise tous les logs pour faciliter le monitoring et le debugging
 * 
 * En développement : logs dans la console
 * En production : peut être étendu pour envoyer vers Sentry, LogRocket, etc.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogContext {
  userId?: string;
  action?: string;
  component?: string;
  [key: string]: unknown;
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: Error;
  timestamp: number;
}

/**
 * Configuration du logger
 */
const LOGGER_CONFIG = {
  // En développement, logger tout
  // En production, logger seulement warn et error
  minLevel: import.meta.env.DEV ? "debug" : "warn",
  
  // Service externe (Sentry, LogRocket, etc.) - à configurer si nécessaire
  externalService: {
    enabled: false,
    // url: "https://your-logging-service.com/api/logs",
  },
} as const;

/**
 * Niveaux de log avec priorités
 */
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Vérifie si un niveau de log doit être enregistré
 */
function shouldLog(level: LogLevel): boolean {
  const minLevelPriority = LOG_LEVELS[LOGGER_CONFIG.minLevel];
  const levelPriority = LOG_LEVELS[level];
  return levelPriority >= minLevelPriority;
}

/**
 * Formate un log entry pour l'affichage
 */
function formatLogEntry(entry: LogEntry): string {
  const timestamp = new Date(entry.timestamp).toISOString();
  const contextStr = entry.context
    ? ` [${Object.entries(entry.context)
        .map(([k, v]) => `${k}=${v}`)
        .join(", ")}]`
    : "";
  const errorStr = entry.error ? `\nError: ${entry.error.message}\n${entry.error.stack}` : "";
  
  return `[${timestamp}] [${entry.level.toUpperCase()}] ${entry.message}${contextStr}${errorStr}`;
}

/**
 * Envoie un log vers un service externe (si configuré)
 */
async function sendToExternalService(entry: LogEntry): Promise<void> {
  if (!LOGGER_CONFIG.externalService.enabled) {
    return;
  }

  try {
    // Exemple d'intégration avec un service externe
    // await fetch(LOGGER_CONFIG.externalService.url, {
    //   method: "POST",
    //   headers: { "Content-Type": "application/json" },
    //   body: JSON.stringify(entry),
    // });
  } catch (error) {
    // Ne pas logger l'erreur de logging pour éviter les boucles infinies
    console.error("Failed to send log to external service:", error);
  }
}

/**
 * Fonction de logging générique
 */
function log(level: LogLevel, message: string, context?: LogContext, error?: Error): void {
  if (!shouldLog(level)) {
    return;
  }

  const entry: LogEntry = {
    level,
    message,
    context,
    error,
    timestamp: Date.now(),
  };

  // Logger dans la console selon le niveau
  const formattedMessage = formatLogEntry(entry);
  
  switch (level) {
    case "debug":
      console.debug(formattedMessage);
      break;
    case "info":
      console.info(formattedMessage);
      break;
    case "warn":
      console.warn(formattedMessage);
      break;
    case "error":
      console.error(formattedMessage);
      if (error) {
        console.error(error);
      }
      break;
  }

  // Envoyer vers service externe (async, ne bloque pas)
  sendToExternalService(entry).catch(() => {
    // Ignorer les erreurs d'envoi pour ne pas bloquer l'application
  });
}

/**
 * Logger pour les erreurs d'authentification
 * 
 * @param message - Message d'erreur
 * @param context - Contexte (userId, action, component, etc.)
 * @param error - Erreur optionnelle
 */
export function logAuthError(message: string, context?: LogContext, error?: Error): void {
  log("error", `[AUTH] ${message}`, context, error);
}

/**
 * Logger pour les erreurs de réseau
 * 
 * @param message - Message d'erreur
 * @param context - Contexte
 * @param error - Erreur optionnelle
 */
export function logNetworkError(message: string, context?: LogContext, error?: Error): void {
  log("error", `[NETWORK] ${message}`, context, error);
}

/**
 * Logger pour les erreurs de validation
 * 
 * @param message - Message d'erreur
 * @param context - Contexte
 */
export function logValidationError(message: string, context?: LogContext): void {
  log("warn", `[VALIDATION] ${message}`, context);
}

/**
 * Logger pour les erreurs de sécurité
 * 
 * @param message - Message d'erreur
 * @param context - Contexte
 * @param error - Erreur optionnelle
 */
export function logSecurityError(message: string, context?: LogContext, error?: Error): void {
  log("error", `[SECURITY] ${message}`, context, error);
}

/**
 * Logger pour les informations de debug
 * 
 * @param message - Message de debug
 * @param context - Contexte
 */
export function logDebug(message: string, context?: LogContext): void {
  log("debug", `[DEBUG] ${message}`, context);
}

/**
 * Logger pour les informations générales
 * 
 * @param message - Message d'information
 * @param context - Contexte
 */
export function logInfo(message: string, context?: LogContext): void {
  log("info", `[INFO] ${message}`, context);
}

/**
 * Logger pour les avertissements
 * 
 * @param message - Message d'avertissement
 * @param context - Contexte
 */
export function logWarning(message: string, context?: LogContext): void {
  log("warn", `[WARN] ${message}`, context);
}

/**
 * Logger générique
 * 
 * @param level - Niveau de log
 * @param message - Message
 * @param context - Contexte
 * @param error - Erreur optionnelle
 */
export function logMessage(level: LogLevel, message: string, context?: LogContext, error?: Error): void {
  log(level, message, context, error);
}

/**
 * Crée un contexte de log à partir d'un ErrorContext
 */
export function createLogContext(
  userId?: string,
  action?: string,
  component?: string,
  additional?: Record<string, unknown>
): LogContext {
  return {
    userId,
    action,
    component,
    ...additional,
  };
}

